/**
 * InvoiceImportDialog Component
 * 
 * Simplified dialog for importing single PDF or XML invoices.
 */

import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle, AlertCircle, X, Loader2, FileCode } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const InvoiceImportDialog = ({ 
  open, 
  onOpenChange, 
  importType = 'pdf', // 'pdf' or 'xml'
  condominiumId,
  onSuccess
}) => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const config = importType === 'xml' ? {
    title: 'Importa Fattura XML',
    description: 'Importa una fattura elettronica in formato FatturaPA o XML',
    endpoint: '/api/import/invoice-xml',
    accept: '.xml,.p7m',
    icon: FileCode,
    iconColor: 'text-orange-600',
    bgColor: 'border-orange-500 bg-orange-50',
    requirements: [
      'Formato FatturaPA (Fattura Elettronica Italiana)',
      'File .xml o .p7m (fatture firmate)',
      'Dati estratti automaticamente con alta precisione'
    ]
  } : {
    title: 'Importa Fattura PDF',
    description: 'Importa una fattura o ricevuta in formato PDF',
    endpoint: '/api/import/invoice-pdf',
    accept: '.pdf',
    icon: FileText,
    iconColor: 'text-red-600',
    bgColor: 'border-red-500 bg-red-50',
    requirements: [
      'Il PDF deve contenere testo selezionabile',
      'Vengono estratti: Fornitore, P.IVA, Importi, Data',
      'I dati richiedono una verifica manuale'
    ]
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    const fileName = file.name.toLowerCase();
    const validExtensions = config.accept.split(',');
    const isValid = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValid) {
      toast.error(`Formato non valido. Usa: ${config.accept}`);
      return;
    }
    
    setSelectedFile(file);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !condominiumId) return;
    
    setUploading(true);
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('condominium_id', condominiumId);
      
      const response = await fetch(`${API_URL}${config.endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setResult({
          success: true,
          ...data
        });
        toast.success(data.message || 'Fattura importata');
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        setResult({
          success: false,
          message: data.detail || 'Errore durante l\'importazione'
        });
        toast.error(data.detail || 'Errore durante l\'importazione');
      }
    } catch (error) {
      console.error('Import error:', error);
      setResult({
        success: false,
        message: 'Errore di connessione'
      });
      toast.error('Errore di connessione');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    onOpenChange(false);
  };

  const IconComponent = config.icon;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconComponent className={`h-5 w-5 ${config.iconColor}`} />
            {config.title}
          </DialogTitle>
          <DialogDescription>
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Requirements */}
          <div className={`p-3 rounded-lg ${importType === 'xml' ? 'bg-orange-50' : 'bg-red-50'}`}>
            <p className={`font-medium text-sm mb-2 ${importType === 'xml' ? 'text-orange-800' : 'text-red-800'}`}>
              Requisiti:
            </p>
            <ul className={`list-disc list-inside space-y-1 text-xs ${importType === 'xml' ? 'text-orange-700' : 'text-red-700'}`}>
              {config.requirements.map((req, i) => (
                <li key={i}>{req}</li>
              ))}
            </ul>
          </div>

          {/* Drop Zone */}
          <div
            className={`
              border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
              ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
              ${selectedFile ? config.bgColor : ''}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={config.accept}
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <IconComponent className={`h-10 w-10 ${config.iconColor}`} />
                <div className="text-left">
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setResult(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Trascina il file qui oppure <span className="text-blue-600 font-medium">sfoglia</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Formati: {config.accept}
                </p>
              </>
            )}
          </div>

          {/* Upload Progress/Result */}
          {uploading && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Estrazione dati in corso...
            </div>
          )}

          {/* Result */}
          {result && (
            <Alert className={result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
              {result.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertTitle className={result.success ? 'text-green-800' : 'text-red-800'}>
                {result.success ? 'Fattura importata' : 'Errore'}
              </AlertTitle>
              <AlertDescription className={result.success ? 'text-green-700' : 'text-red-700'}>
                {result.message}
                
                {/* Show extracted data */}
                {result.extracted_data && (
                  <div className="mt-3 p-2 bg-white rounded border text-xs">
                    <p className="font-medium text-gray-800 mb-2">Dati estratti:</p>
                    <div className="grid grid-cols-2 gap-1">
                      {result.extracted_data.supplier && (
                        <p><span className="text-gray-500">Fornitore:</span> {result.extracted_data.supplier}</p>
                      )}
                      {result.extracted_data.vat_number && (
                        <p><span className="text-gray-500">P.IVA:</span> {result.extracted_data.vat_number}</p>
                      )}
                      {result.extracted_data.invoice_number && (
                        <p><span className="text-gray-500">N. Fattura:</span> {result.extracted_data.invoice_number}</p>
                      )}
                      {result.extracted_data.invoice_date && (
                        <p><span className="text-gray-500">Data:</span> {result.extracted_data.invoice_date}</p>
                      )}
                      {result.extracted_data.taxable !== undefined && (
                        <p><span className="text-gray-500">Imponibile:</span> €{result.extracted_data.taxable.toFixed(2)}</p>
                      )}
                      {result.extracted_data.vat !== undefined && (
                        <p><span className="text-gray-500">IVA:</span> €{result.extracted_data.vat.toFixed(2)}</p>
                      )}
                      {result.extracted_data.total !== undefined && (
                        <p className="col-span-2 font-medium mt-1 pt-1 border-t">
                          <span className="text-gray-500">Totale:</span> €{result.extracted_data.total.toFixed(2)}
                        </p>
                      )}
                    </div>
                    {result.needs_review && (
                      <p className="mt-2 text-amber-600 text-xs">
                        ⚠️ Verificare i dati nella sezione Spese
                      </p>
                    )}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            {result?.success ? 'Chiudi' : 'Annulla'}
          </Button>
          {!result?.success && (
            <Button 
              onClick={handleUpload} 
              disabled={!selectedFile || uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importa...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importa Fattura
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceImportDialog;
